import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Button,
  CssBaseline,
  Typography,
  Card,
  CardMedia,
  TextField,
} from '@material-ui/core';
import SaveIcon from '@material-ui/icons/Save';
import { useTranslation } from 'react-i18next';
import { Formik, Form, FormikHelpers, getIn } from 'formik';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ApplicationState } from '../../stores';
import { useStyles } from './style';
import { validationSchema } from './validaton';
import { RegisterPersonModel, registerPersonBlankObj } from '../../models/registerPersonModel';
import { invitationDetailsReq, registerUserReq } from '../../api/registrationApi';
import { useQuery } from '../../hooks/useQuery';
import { showSuccess, showError } from '../../services/notification';
import htsLogo from '../../images/ztsh-logo.png';
import { PersonProfileCp } from '../../components/personProfile/personProfileCp';

function UserRegister() {
  const history = useHistory();
  const queryParam = useQuery();
  const userRegisterStyles = useStyles();
  const { t } = useTranslation();

  const isLoading = useSelector<ApplicationState, boolean>(state => state.loader.loading);
  const [avatar, setAvatar] = useState<File>();
  const [userInitialData, setUserInitialData] = useState<RegisterPersonModel>();
  const invitationCode = queryParam.get('invcode')?.trim();

  useEffect(() => {
    async function getInvitedUserInfo() {
      if (!invitationCode) {
        showError(t('invalid_invitation_code'));
        return;
      }

      let registerPersonState = { ...registerPersonBlankObj };
      const response = await invitationDetailsReq(invitationCode);
      if (response && response.data) {
        let invitedUserData = JSON.parse(response.data);

        registerPersonState.roles = [
          {
            providerId: invitedUserData.providerCtx,
            roleType: invitedUserData.invUserModel.userRole,
          },
        ];
        registerPersonState.coms.emailAddresses[0].emailAddress =
          invitedUserData.invUserModel.emailAddress;
      } else {
        showError(t('invalid_invitation_code'));
        return;
      }

      setUserInitialData(registerPersonState);
    }
    getInvitedUserInfo();
  }, [setUserInitialData, invitationCode, t]);

  async function handleRegistrationSubmit(
    values: RegisterPersonModel,
    actions: FormikHelpers<RegisterPersonModel>,
  ) {
    if (!invitationCode) return;

    const userRegistrationDetails = { ...values } as RegisterPersonModel;
    userRegistrationDetails.passportGivenName = userRegistrationDetails.preferredGivenName;
    userRegistrationDetails.passportFamilyName = userRegistrationDetails.preferredFamilyName;

    try {
      await registerUserReq(userRegistrationDetails, invitationCode, avatar);
      showSuccess(t('account_created'));
      actions.resetForm();
      history.push('/login');
    } catch {
      showError(t('account_creation_error'));
    }
  }

  return (
    <Container className={userRegisterStyles.rootBox}>
      <CssBaseline />
      <Formik
        initialValues={userInitialData || registerPersonBlankObj}
        enableReinitialize={true}
        onSubmit={handleRegistrationSubmit}
        validationSchema={validationSchema}
      >
        {formikProps => {
          const { isValid, values, errors, touched, handleChange, setFieldTouched } = formikProps;

          function isFieldError(fieldPath: string) {
            return getIn(touched, fieldPath) && !!getIn(errors, fieldPath);
          }

          function getHelperText(fieldPath: string) {
            if (getIn(touched, fieldPath)) return getIn(errors, fieldPath) || '';

            return '';
          }

          return (
            <Form>
              <Grid
                container
                spacing={1}
                justify='flex-start'
                className={userRegisterStyles.pageTitleContainer}
              >
                <Grid item lg={1} className={userRegisterStyles.logoGrid}>
                  <Card elevation={0} className={userRegisterStyles.logoCard}>
                    <CardMedia
                      component='img'
                      image={htsLogo}
                      title={t('croatian_tennis_federation')}
                    />
                  </Card>
                </Grid>
                <Grid item>
                  <Typography
                    component='h4'
                    variant='h4'
                    display='block'
                    className={userRegisterStyles.welcome}
                  >
                    {t('register_page_welcome')}
                  </Typography>
                  <Typography
                    component='h6'
                    variant='subtitle1'
                    className={userRegisterStyles.welcomeText}
                  >
                    {t('register_page_text')}
                  </Typography>
                </Grid>
              </Grid>

              <PersonProfileCp {...formikProps} setAvatar={setAvatar} />

              <Grid item xs={6}>
                <TextField
                  variant='outlined'
                  margin='normal'
                  id='password'
                  name='password'
                  required
                  label={t('password')}
                  fullWidth
                  type='password'
                  error={isFieldError('password')}
                  helperText={t(getHelperText('password'))}
                  onBlur={() => setFieldTouched('password')}
                  value={values.password || ''}
                  onChange={handleChange('password')}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant='outlined'
                  margin='normal'
                  id='confirmPassword'
                  name='confirmPassword'
                  required
                  label={t('confirm_password')}
                  fullWidth
                  type='password'
                  error={isFieldError('confirmPassword')}
                  helperText={t(getHelperText('confirmPassword'))}
                  onBlur={() => setFieldTouched('confirmPassword')}
                  value={undefined}
                  onChange={handleChange('confirmPassword')}
                />
              </Grid>

              <Grid item xs className={userRegisterStyles.submitButtonGrid}>
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  size='large'
                  className={userRegisterStyles.button}
                  disabled={isLoading || !isValid}
                  startIcon={<SaveIcon />}
                >
                  {t('save')}
                </Button>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
}

export default UserRegister;
